import { AlertCircle, CalendarRange, Loader2, ReceiptText } from 'lucide-react'
import { useConfigForm } from './useSingleConfig'
import { Field, NumberInput, PageHead, SaveBar, SectionCard, SelectInput, SwitchRow } from './ui'

type El = Record<string, unknown>
const isYes = (v: unknown) => v === true || v === 'true' || v === 1 || v === '1'
const numStr = (v: unknown, fallback = '') => (v == null || v === '' ? fallback : String(v))

type Form = {
  preventBookingLessThanHours: string
  bookingMaxDays: string
  bookingHoursSubdivision: string
  enableInvoice: boolean
}

// Mirrors generalConfiguration.bookingHoursSubdivision.attributes.values in the Yep model.
const SUBDIVISIONS = [
  { value: '5', label: 'Ogni 5 minuti' },
  { value: '10', label: 'Ogni 10 minuti' },
  { value: '15', label: 'Ogni 15 minuti' },
  { value: '30', label: 'Ogni 30 minuti' },
]

export function GeneralConfigPage() {
  const { query, form, dirty, patch, reset, commit, saving, ok, error } = useConfigForm<Form>(
    'generalConfiguration',
    (el) => ({
      preventBookingLessThanHours: numStr(el.preventBookingLessThanHours),
      bookingMaxDays: numStr(el.bookingMaxDays, '10'),
      bookingHoursSubdivision: numStr(el.bookingHoursSubdivision, '15'),
      enableInvoice: isYes(el.enableInvoice),
    }),
  )

  function save() {
    commit((f) => ({
      preventBookingLessThanHours: Number(f.preventBookingLessThanHours) || 0,
      bookingMaxDays: Number(f.bookingMaxDays) || 0,
      bookingHoursSubdivision: Number(f.bookingHoursSubdivision) || 15,
      enableInvoice: f.enableInvoice,
    }))
  }

  return (
    <div className="w-full px-6 pb-16 pt-7 lg:px-9">
      <PageHead
        title="Configurazione · Generale"
        description="Parametri del motore di prenotazione e fatturazione. Si applicano a tutti i canali di vendita."
      />

      {query.isError ? (
        <div className="cv-panel px-4 py-16 text-center text-sm text-muted">
          <AlertCircle className="mx-auto mb-2 h-5 w-5 text-venetian" /> Impossibile caricare la configurazione.
        </div>
      ) : !form ? (
        <div className="cv-panel flex items-center justify-center py-20 text-muted">
          <Loader2 className="h-5 w-5 animate-spin" />
        </div>
      ) : (
        <>
          <div className="grid grid-cols-1 items-start gap-[18px] lg:grid-cols-2">
            <SectionCard
              title="Motore di prenotazione"
              icon={CalendarRange}
              desc="Finestra e granularità con cui i clienti possono prenotare."
            >
              <div className="space-y-4">
                <Field
                  label="Anticipo minimo di prenotazione"
                  hint="Le prenotazioni sotto questa soglia di anticipo vengono bloccate."
                >
                  <NumberInput
                    value={form.preventBookingLessThanHours}
                    min={0}
                    suffix="ore"
                    disabled={saving}
                    onChange={(v) => patch({ preventBookingLessThanHours: v })}
                  />
                </Field>
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                  <Field label="Giorni massimi prenotabili" hint="Quanto in anticipo si può prenotare.">
                    <NumberInput
                      value={form.bookingMaxDays}
                      min={1}
                      suffix="giorni"
                      disabled={saving}
                      onChange={(v) => patch({ bookingMaxDays: v })}
                    />
                  </Field>
                  <Field label="Suddivisione fasce orarie" hint="Granularità degli slot selezionabili.">
                    <SelectInput
                      value={form.bookingHoursSubdivision}
                      options={SUBDIVISIONS}
                      disabled={saving}
                      onChange={(v) => patch({ bookingHoursSubdivision: v })}
                    />
                  </Field>
                </div>
              </div>
            </SectionCard>

            <SectionCard title="Fatturazione" icon={ReceiptText} desc="Emissione documenti fiscali per gli acquisti.">
              <SwitchRow
                title="Abilita fatturazione"
                desc="Mostra i campi di fatturazione in fase di acquisto ed emette i documenti fiscali."
                on={form.enableInvoice}
                disabled={saving}
                onToggle={(v) => patch({ enableInvoice: v })}
              />
            </SectionCard>
          </div>

          <SaveBar dirty={dirty} saving={saving} error={error} ok={ok} onSave={save} onReset={reset} />
        </>
      )}
    </div>
  )
}
